/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ClosedCaptionPreset } from "../enums";
import type { IClosedCaptionStyler } from "../interfaces";
import { DeviceModel } from "../../device/DeviceModel";
import { Log } from "../../logging/Log";
import type { TClosedCaptionStyle } from "../types";
import {
  CLOSED_CAPTION_PRESETS,
  DEFAULT_CLOSED_CAPTION_PRESET,
  getClosedCaptionBaseFontSizeVw
} from "./ClosedCaptionStyleConfig";

/**
 * Runtime closed caption styler for Shaka Player.
 *
 * It owns a single injected `<style>` element whose rules target the Shaka caption elements,
 * and rewrites them whenever the user selects a caption preset.
 *
 * Shaka's UITextDisplayer renders captions as HTML inside
 * `.shaka-text-container` (region → wrapper → span). Those elements are styled
 * directly (with `!important`), and native `video::cue` is kept as a fallback.
 */
export class ClosedCaptionStyler implements IClosedCaptionStyler {
  private static readonly TAG: string = "ClosedCaptionStyler";

  /** Id of the injected style element so it can be reused/located. */
  private static readonly STYLE_ELEMENT_ID: string =
    "unity-closed-caption-style";

  /** Default Shaka caption container selector. */
  private static readonly DEFAULT_CONTAINER_SELECTOR: string =
    ".shaka-text-container";

  private readonly mContainerSelector: string;
  private mStyleEl: HTMLStyleElement | null = null;
  private mCurrentPreset: ClosedCaptionPreset;

  /**
   * @param containerSelector - Optional override for the Shaka caption
   *   container selector (defaults to `.shaka-text-container`).
   */
  public constructor(containerSelector?: string) {
    this.mContainerSelector =
      containerSelector !== null &&
      containerSelector !== undefined &&
      containerSelector.length > 0
        ? containerSelector
        : ClosedCaptionStyler.DEFAULT_CONTAINER_SELECTOR;
    this.mCurrentPreset = DEFAULT_CLOSED_CAPTION_PRESET;
  }

  public applyPreset(preset: ClosedCaptionPreset): void {
    this.mCurrentPreset = preset;
    this.renderCueRule();
  }

  public resetPreset(): void {
    this.mCurrentPreset = DEFAULT_CLOSED_CAPTION_PRESET;
    this.renderCueRule();
  }

  public getCurrentPreset(): ClosedCaptionPreset {
    return this.mCurrentPreset;
  }

  public getCurrentStyle(): TClosedCaptionStyle {
    return { ...this.resolveStyle() };
  }

  public destroy(): void {
    if (this.mStyleEl !== null && this.mStyleEl.parentNode !== null) {
      this.mStyleEl.parentNode.removeChild(this.mStyleEl);
    }
    this.mStyleEl = null;
  }

  /**
   * Replace the single `::cue` rule in the injected style sheet with one built
   * from the current style. Mirrors the legacy delete-then-insert sequence.
   */
  private renderCueRule(): void {
    const styleEl = this.ensureStyleElement();
    if (styleEl === null) {
      return;
    }

    const sheet = styleEl.sheet;
    if (sheet === null) {
      Log.w(
        ClosedCaptionStyler.TAG,
        "renderCueRule(): Style sheet unavailable; ::cue styling not supported."
      );
      return;
    }

    while (sheet.cssRules.length > 0) {
      sheet.deleteRule(0);
    }
    const rules = this.buildCueRules();
    rules.forEach((rule) => {
      try {
        sheet.insertRule(rule, sheet.cssRules.length);
      } catch (error: unknown) {
        Log.w(ClosedCaptionStyler.TAG, "renderCueRule(): rule skipped", error);
      }
    });
  }

  /**
   * Locate or create the shared `<style>` element in the document head.
   *
   * @returns The style element, or null when the DOM is unavailable.
   */
  private ensureStyleElement(): HTMLStyleElement | null {
    if (this.mStyleEl !== null) {
      return this.mStyleEl;
    }

    if (typeof document === "undefined" || document.head === null) {
      Log.w(
        ClosedCaptionStyler.TAG,
        "ensureStyleElement(): Document not available."
      );
      return null;
    }

    const existing = document.getElementById(
      ClosedCaptionStyler.STYLE_ELEMENT_ID
    );
    if (existing instanceof HTMLStyleElement) {
      this.mStyleEl = existing;
      return existing;
    }

    const styleEl = document.createElement("style");
    styleEl.id = ClosedCaptionStyler.STYLE_ELEMENT_ID;
    document.head.appendChild(styleEl);
    this.mStyleEl = styleEl;
    return styleEl;
  }

  /**
   * Resolve the style for the current preset, falling back to the default
   * preset if the registry has no matching entry.
   *
   * @returns The resolved caption style.
   */
  private resolveStyle(): TClosedCaptionStyle {
    const style = CLOSED_CAPTION_PRESETS[this.mCurrentPreset];
    if (style === null || style === undefined) {
      return CLOSED_CAPTION_PRESETS[DEFAULT_CLOSED_CAPTION_PRESET];
    }
    return style;
  }

  /**
   * Build the CSS rules for the current preset's style.
   *
   * Shaka's UITextDisplayer renders captions as HTML (`.shaka-text-container`
   * with nested regions/wrappers/spans) and sets colors, font size and the
   * background as inline styles. `!important` is required to override those
   * inline styles. Native `::cue` is kept as a fallback for the browser's
   * built-in `<track>` rendering.
   *
   * @returns The CSS rule strings to insert.
   */
  private buildCueRules(): string[] {
    const style = this.resolveStyle();
    const sel = this.mContainerSelector;
    const baseFontSizeVw = getClosedCaptionBaseFontSizeVw(
      DeviceModel.isTvDevice()
    );
    const fontSizeVw = style.fontSizeMultiplier * baseFontSizeVw;
    const appearance = [
      `color: ${style.color} !important;`,
      `font-size: ${fontSizeVw}vw !important;`,
      `font-family: ${style.fontFamily} !important;`,
      `font-weight: ${style.fontWeight} !important;`,
      `font-style: ${style.fontStyle} !important;`,
      `font-variant: ${style.fontVariant} !important;`
    ].join(" ");
    const background = `background-color: ${style.backgroundColor} !important;`;

    return [
      // Text appearance on the caption container and every nested element.
      `${sel}, ${sel} * { ${appearance} }`,
      // Background only on the text-bearing spans so it hugs the words rather
      // than filling the full-width container/region.
      `${sel} .shaka-text-wrapper, ${sel} span { ${background} }`,
      // Native browser track rendering fallback.
      `video::cue { ${appearance} ${background} }`
    ];
  }
}
